import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { g, p, api } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { ShieldCheck, FileText, Eye, EyeOff, ArrowDown, Eraser, Check, Loader2 } from "lucide-react";

const VEDE = [
  "Nome del locale, referente, email, piano e scadenza dell'abbonamento",
  "Quanti piatti, tavoli e persone hai inserito (solo il numero)",
  "Se hai già chiuso almeno un conto con l'app: sì o no",
  "Quali funzioni hai attivato e le richieste di assistenza che ci mandi",
];
const NON_VEDE = [
  "I tuoi incassi: nessun importo, nessun totale, nessuna statistica",
  "Conti, scontrini, pagamenti, mance, fondi cassa e conteggi dei contanti",
  "Comande, piatti venduti e orari del personale",
  "Nomi e PIN dei dipendenti, prenotazioni e dati dei tuoi clienti",
];
const PUNTI = [
  ["Tu sei il titolare dei dati", "I dati del tuo locale restano tuoi. Noi li trattiamo come responsabili, solo per farti funzionare l'app e solo secondo le tue istruzioni."],
  ["Non sostituisce il registratore di cassa", "Conti e riepiloghi stampati dall'app non sono documenti fiscali: gli obblighi fiscali restano a carico dell'attività."],
  ["Dove stanno i dati", "Su infrastruttura cloud di terzi (Stati Uniti, Unione Europea, India) con fornitori nominati responsabili e vincolati al GDPR."],
  ["Copia sempre disponibile", "Puoi scaricare in qualsiasi momento una copia completa dei tuoi dati dalla pagina Backup e chiedere la cancellazione definitiva."],
  ["Il personale è informato", "Le persone che inserisci devono sapere che le azioni nel gestionale sono registrate (chi ha incassato, chi ha annullato, chi ha scontato)."],
];

/** Firma del blocco pre-configurazione: si legge tutto, si firma col dito, il PDF arriva per email. */
const Tela = ({ onCambio }) => {
  const ref = useRef(null);
  const disegna = useRef(false);
  const vuota = useRef(true);

  useEffect(() => {
    const c = ref.current;
    const r = c.getBoundingClientRect();
    const dpr = window.devicePixelRatio || 1;
    c.width = r.width * dpr;
    c.height = r.height * dpr;
    const ctx = c.getContext("2d");
    ctx.scale(dpr, dpr);
    ctx.lineWidth = 2.4;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.strokeStyle = "#0f172a";
  }, []);

  const punto = (e) => {
    const r = ref.current.getBoundingClientRect();
    const t = e.touches?.[0] || e;
    return { x: t.clientX - r.left, y: t.clientY - r.top };
  };
  const giu = (e) => {
    e.preventDefault();
    disegna.current = true;
    const ctx = ref.current.getContext("2d");
    const { x, y } = punto(e);
    ctx.beginPath();
    ctx.moveTo(x, y);
  };
  const muovi = (e) => {
    if (!disegna.current) return;
    e.preventDefault();
    const ctx = ref.current.getContext("2d");
    const { x, y } = punto(e);
    ctx.lineTo(x, y);
    ctx.stroke();
    if (vuota.current) {
      vuota.current = false;
      onCambio(ref.current.toDataURL("image/png"));
    }
  };
  const su = () => {
    if (!disegna.current) return;
    disegna.current = false;
    if (!vuota.current) onCambio(ref.current.toDataURL("image/png"));
  };
  const pulisci = () => {
    const c = ref.current;
    c.getContext("2d").clearRect(0, 0, c.width, c.height);
    vuota.current = true;
    onCambio("");
  };

  return (
    <div>
      <canvas
        ref={ref}
        data-testid="firma-canvas"
        className="h-36 w-full touch-none rounded-xl border-2 border-dashed border-slate-300 bg-white"
        onMouseDown={giu}
        onMouseMove={muovi}
        onMouseUp={su}
        onMouseLeave={su}
        onTouchStart={giu}
        onTouchMove={muovi}
        onTouchEnd={su}
      />
      <button
        onClick={pulisci}
        className="mt-1 flex items-center gap-1.5 text-xs font-semibold text-slate-500 hover:text-slate-800"
        data-testid="firma-pulisci"
      >
        <Eraser className="h-3.5 w-3.5" /> Cancella e rifai
      </button>
    </div>
  );
};

export const FirmaLegale = ({ onFirmato, attivita }) => {
  const { user, demoMode, tenant } = useApp();
  const [st, setSt] = useState(null);
  const [letto, setLetto] = useState(false);
  const [ok, setOk] = useState(false);
  const [nome, setNome] = useState(user?.name || "");
  const [email, setEmail] = useState("");
  const [firma, setFirma] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (demoMode) return;
    // Regola di sicurezza: questo blocco non deve MAI impedire di lavorare. Se il server non
    // risponde (rete del locale, riavvio, versione vecchia) si passa oltre e si richiede dopo.
    const sblocca = setTimeout(() => onFirmato?.(), 8000);
    g("/privacy/status")
      .then((r) => {
        clearTimeout(sblocca);
        setSt(r);
        setEmail(r.email || "");
        if (r.accepted) onFirmato?.();
      })
      .catch(() => {
        clearTimeout(sblocca);
        onFirmato?.();
      });
    return () => clearTimeout(sblocca);
  }, [demoMode]); // eslint-disable-line react-hooks/exhaustive-deps

  if (demoMode) return null;
  if (!st || st.accepted)
    // Finché lo stato non arriva si mostra l'attesa, non una pagina vuota: se il server non
    // risponde entro 8 secondi il blocco si apre da solo (vedi il timer sopra).
    return (
      <div className="fixed inset-0 z-[70] flex flex-col items-center justify-center gap-4 bg-slate-950 px-8 text-center"
           data-testid="firma-legale-attesa">
        <span className="flex h-20 w-20 items-center justify-center rounded-2xl bg-amber-500 font-head text-3xl font-bold text-slate-950">
          RH
        </span>
        <p className="font-head text-lg font-bold text-white">{attivita || "Reprehendo"}</p>
        <p className="flex items-center gap-2 text-sm text-slate-400">
          <Loader2 className="h-4 w-4 animate-spin" /> Controllo i documenti…
        </p>
      </div>
    );

  const scorri = (e) => {
    const el = e.currentTarget;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 24) setLetto(true);
  };

  const leggi = async () => {
    const r = await api.get("/tenant/privacy.pdf", { responseType: "blob" });
    window.open(URL.createObjectURL(r.data), "_blank");
  };

  const firmaOra = async () => {
    setBusy(true);
    try {
      const r = await p("/privacy/accept", { by: nome.trim(), role: user?.role || "titolare", email: email.trim(), signature: firma });
      toast.success("Documento firmato", {
        description: r?.sent_to?.length
          ? `Copia PDF inviata a ${r.sent_to.join(" e ")}`
          : "Firma registrata. La copia PDF è scaricabile da Impostazioni.",
      });
      setSt({ ...st, accepted: true });
      onFirmato?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Firma non registrata, riprova");
    } finally {
      setBusy(false);
    }
  };

  const pronto = letto && ok && nome.trim().length > 2 && firma;

  return (
    <div className="fixed inset-0 z-[70] flex flex-col bg-slate-950/95 backdrop-blur-sm" data-testid="firma-legale">
      <div className="mx-auto flex h-full w-full max-w-2xl flex-col p-3 sm:p-6">
        <div className="flex items-center gap-2 pb-3 text-amber-400">
          <ShieldCheck className="h-5 w-5" />
          <div className="min-w-0">
            <p className="font-head text-base font-bold text-white sm:text-lg">Prima di configurare: leggi e firma</p>
            <p className="truncate text-xs text-slate-400">
              {tenant?.name || st.locale} · informativa versione {st.version} del {st.date}
            </p>
          </div>
        </div>

        <div
          onScroll={scorri}
          className="min-h-0 flex-1 space-y-3 overflow-y-auto rounded-2xl bg-white p-4 text-slate-700"
          data-testid="firma-testo"
        >
          <p className="text-sm">
            Questo documento è l'informativa privacy e la nomina a responsabile del trattamento. Scorri
            fino in fondo: il tasto per firmare si accende quando hai letto tutto.
          </p>

          <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-3" data-testid="privacy-vede">
            <p className="flex items-center gap-2 text-sm font-semibold text-emerald-900">
              <Eye className="h-4 w-4" /> Cosa vede Reprehendo
            </p>
            <ul className="mt-2 space-y-1 text-xs text-emerald-900/80">
              {VEDE.map((x) => <li key={x}>· {x}</li>)}
            </ul>
          </div>

          <div className="rounded-xl border border-slate-200 bg-slate-50 p-3" data-testid="privacy-non-vede">
            <p className="flex items-center gap-2 text-sm font-semibold text-slate-900">
              <EyeOff className="h-4 w-4" /> Cosa non vede mai
            </p>
            <ul className="mt-2 space-y-1 text-xs text-slate-600">
              {NON_VEDE.map((x) => <li key={x}>· {x}</li>)}
            </ul>
          </div>

          {PUNTI.map(([t, d]) => (
            <div key={t}>
              <p className="text-sm font-semibold text-slate-900">{t}</p>
              <p className="text-xs text-slate-600">{d}</p>
            </div>
          ))}

          <button
            onClick={leggi}
            className="flex items-center gap-2 text-sm font-semibold text-amber-700 hover:underline"
            data-testid="privacy-read-btn"
          >
            <FileText className="h-4 w-4" /> Apri il documento completo in PDF
          </button>

          <p className="rounded-xl bg-amber-50 p-3 text-xs text-amber-900">
            Firmando, una copia in PDF con la tua firma, data, ora e impronta del documento viene
            archiviata e inviata per email a te e al fornitore del servizio.
          </p>
          <p className="text-center text-[11px] font-semibold text-emerald-700" data-testid="firma-fine-testo">
            {letto ? "Hai letto tutto: puoi firmare qui sotto." : "Continua a scorrere…"}
          </p>
        </div>

        {!letto && (
          <p className="flex items-center justify-center gap-1.5 py-2 text-xs font-semibold text-amber-400">
            <ArrowDown className="h-3.5 w-3.5 animate-bounce" /> Scorri fino in fondo per firmare
          </p>
        )}

        <div className="mt-3 space-y-3 rounded-2xl bg-white p-4">
          <div className="grid gap-2 sm:grid-cols-2">
            <div>
              <label className="text-xs font-semibold text-slate-500">Nome e cognome di chi firma</label>
              <Input value={nome} onChange={(e) => setNome(e.target.value)} placeholder="Mario Rossi" data-testid="firma-nome" />
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-500">Email per la copia archiviata</label>
              <Input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="tua@email.it" data-testid="firma-email" />
            </div>
          </div>

          <div>
            <label className="text-xs font-semibold text-slate-500">Firma qui col dito</label>
            <Tela onCambio={setFirma} />
          </div>

          <label className="flex cursor-pointer items-start gap-3 rounded-xl border border-slate-200 p-3">
            <Checkbox checked={ok} disabled={!letto} onCheckedChange={(v) => setOk(!!v)} data-testid="privacy-checkbox" />
            <span className="text-sm">
              Ho letto l'informativa e la accetto come titolare del trattamento dei dati del mio locale.
            </span>
          </label>

          <Button disabled={!pronto || busy} onClick={firmaOra} className="w-full" data-testid="privacy-accept-btn">
            {busy ? <><Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> Firmo e invio…</>
                  : <><Check className="mr-1.5 h-4 w-4" /> Accetto e firmo</>}
          </Button>
        </div>
      </div>
    </div>
  );
};
